import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BuildingIcon, UserIcon, LockIcon, ArrowRightIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { contactDetails } from '../../data/company';
import { faqCategories } from '../../data/faqs';
import { PageHero } from '../../components/ui/PageHero';
import { SectionHeading } from '../../components/ui/SectionHeading';
import { Accordion } from '../../components/ui/Accordion';
import { ContactForm, contactPathways } from '../../components/forms/ContactForm';
const pathways = [{
  icon: BuildingIcon,
  title: 'Employers',
  text: 'Discuss a hiring need or a leadership appointment.',
  to: '/employers#inquiry',
  cta: 'Employer inquiry'
}, {
  icon: UserIcon,
  title: 'Candidates',
  text: 'Register confidentially or ask about an opportunity.',
  to: '/candidates#register',
  cta: 'Candidate registration'
}, {
  icon: LockIcon,
  title: 'Executive search',
  text: 'Begin a confidential search for a senior leader.',
  to: '/executive-search#inquiry',
  cta: 'Confidential inquiry'
}];
const pathwayParam: Record<string, string> = {
  general: contactPathways[0],
  employer: contactPathways[1],
  candidate: contactPathways[2],
  executive: contactPathways[3]
};
export function Contact() {
  usePageMeta('Contact Us', 'Contact Corventa Talent Search about hiring, careers or a confidential executive search.');
  const [params] = useSearchParams();
  const initial = pathwayParam[params.get('pathway') ?? ''] ?? '';
  return <>
      <PageHero crumbs={[{
      label: 'Contact'
    }]} kicker="Contact us" title="Let’s Find the Right Talent." intro="Whether you are hiring, exploring your next move, or considering a confidential search, we would be glad to hear from you." data-id="element-870" data-mp="p0adlr0c8kd5" />

      <section className="bg-ivory py-20" data-id="element-871" data-mp="1950vkpp2872">
        <div className="container-page" data-id="element-872" data-mp="183nvap4bbmp">
          <h2 className="sr-only" data-id="element-873" data-mp="68hlrzx09570">Inquiry pathways</h2>
          <ul className="grid gap-5 md:grid-cols-3" data-id="element-874" data-mp="676hlyzndvfd">
            {pathways.map(p => {
            const Icon = p.icon;
            return <li key={p.title} data-id="element-875" data-mp="3pfo1vpeyhl6n">
                  <Link to={p.to} className="group flex h-full flex-col rounded-xl border border-line bg-white p-7 transition-[border-color,box-shadow,transform] duration-200 ease-premium hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-soft" data-id="element-876" data-mp="3pemhuvfypocs">
                    <Icon className="h-6 w-6 text-gold-700" aria-hidden="true" data-id="element-877" data-mp="3bh7q3k28l4hn" />
                    <span className="mt-5 font-display text-2xl font-semibold text-ink" data-id="element-878" data-mp="36hv1atrt1ncf">{p.title}</span>
                    <span className="mt-2 text-muted" data-id="element-879" data-mp="3lem5dgcfbn8d">{p.text}</span>
                    <span className="mt-auto flex items-center gap-1.5 pt-6 text-sm font-medium text-royal" data-id="element-880" data-mp="3gd6gfd74kwkp">
                      {p.cta}
                      <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-881" data-mp="10p6giqwrm8ld" />
                    </span>
                  </Link>
                </li>;
          })}
          </ul>
        </div>
      </section>

      <section className="bg-white py-20" data-id="element-882" data-mp="187w7z3fmfiwe">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-883" data-mp="u73bwe9o8fr6">
          <div className="lg:col-span-7" data-id="element-884" data-mp="u8wlo2fkzg3e">
            <SectionHeading title="General inquiry" intro="Use this form for any question. Choose the inquiry type so we can route it to the right consultant." data-id="element-885" data-mp="y7n73caop9ao" />
            <div className="mt-10" data-id="element-886" data-mp="1m0c3lj670ena">
              <ContactForm initialPathway={initial} data-id="element-887" data-mp="1qxca1v6yfjex" />
            </div>
          </div>
          <aside className="lg:col-span-5" data-id="element-888" data-mp="1d5xtdv0irub1">
            <div className="rounded-xl bg-midnight p-8 text-white" data-id="element-889" data-mp="1d564f31fgutq">
              <h2 className="font-display text-3xl font-semibold" data-id="element-890" data-mp="1h6hl2j2bq5ps">Business contact details</h2>
              <p className="mt-2 text-sm text-white/60" data-id="element-891" data-mp="24wlputr90bid">Verified details will be published here once confirmed.</p>
              <dl className="mt-8 space-y-5" data-id="element-892" data-mp="29olu2yecpqkr">
                {contactDetails.map(c => <div key={c.label} data-id="element-893" data-mp="m0a8s2vc4drm">
                    <dt className="text-xs text-white/55" data-id="element-894" data-mp="lx5wsjbmodqe">{c.label}</dt>
                    <dd className="mt-1 text-white/90" data-id="element-895" data-mp="gze7zy6a57qf">{c.value}</dd>
                  </div>)}
                <div data-id="element-896" data-mp="c1kzuzlf23ni">
                  <dt className="text-xs text-white/55" data-id="element-897" data-mp="c35n045eikbc">Social media</dt>
                  <dd className="mt-1 text-white/90" data-id="element-898" data-mp="8126h13vkpj7">[Profiles to be added]</dd>
                </div>
              </dl>
            </div>
            <div className="mt-6 rounded-xl bg-ivory p-6 text-sm leading-relaxed text-muted" data-id="element-899" data-mp="zqngq5887sgy">
              <h3 className="font-medium text-ink" data-id="element-900" data-mp="zszyaha2o0th">Privacy notice</h3>
              <p className="mt-2" data-id="element-901" data-mp="uydvh2i23gl0">
                We use the details you provide only to respond to your inquiry. Read our{' '}
                <Link to="/legal/privacy" className="text-royal underline underline-offset-2" data-id="element-902" data-mp="qwis7sgket4l">
                  Privacy Policy
                </Link>{' '}
                for more information.
              </p>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-ivory py-20" data-id="element-903" data-mp="31e3zmihwmlhe">
        <div className="container-page grid gap-12 lg:grid-cols-12" data-id="element-904" data-mp="36b2ux4db0yh6">
          <div className="lg:col-span-4" data-id="element-905" data-mp="36dgnn6kihylk">
            <SectionHeading title="Frequently asked questions" data-id="element-906" data-mp="3acobazp1ruw4" />
            <Link to="/faq" className="mt-6 inline-flex items-center gap-2 font-medium text-royal" data-id="element-907" data-mp="3afafqqgn5ua4">
              All FAQs <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-908" data-mp="3kbdju1v202zp" />
            </Link>
          </div>
          <div className="lg:col-span-8" data-id="element-909" data-mp="3p9yn6a5lr2sd">
            <Accordion items={[faqCategories[0].items[0], faqCategories[1].items[0], faqCategories[2].items[1]]} data-id="element-910" data-mp="3p8x6nq5tho39" />
          </div>
        </div>
      </section>
    </>;
}